import { Box, Skeleton, SkeletonCircle, Text } from '@chakra-ui/react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { formatDistanceToNowStrict } from 'date-fns';
import { trioUITheme } from '@/styles/theme';
import * as Styles from './CandidateBoardCard.styles';
import Menu from '../Menu';
import { MenuItemProps } from '../Menu/Menu.component';
import StatusTag from '../StatusTag/StatusTag.component';

export type CandidateBoardCardProps = {
  firstName: string;
  lastName: string;
  status?: string;
  inStatusSince: string;
  owner: string;
  stack?: string[];
  candidateLink: string;
  menuItems?: MenuItemProps[];
};

const CandidateBoardCard = ({
  firstName,
  lastName,
  status,
  inStatusSince,
  owner,
  stack = [],
  candidateLink,
  menuItems,
}: CandidateBoardCardProps): JSX.Element => (
  <Box
    display="flex"
    alignItems="flex-start"
    flexDirection="column"
    width="260px"
    bg={trioUITheme.colors['neutral-10']}
    boxShadow="0px 0px 6px rgba(0, 0, 0, 0.12)"
    border={`1px solid ${trioUITheme.colors['neutral-40']}`}
    borderRadius="8px"
    p="12px"
  >
    <Box display="flex" width="100%" justifyContent="center" alignItems="flex-start">
      <Styles.StyledUserInitials firstName={firstName} lastName={lastName} />
      <Styles.NameLink to={candidateLink}>
        <Text fontWeight={600} fontSize="16px" color={trioUITheme.colors['neutral-90']}>
          {`${firstName} ${lastName}`}
        </Text>
      </Styles.NameLink>

      {menuItems && <Menu icon={MoreHorizIcon} menuItems={menuItems} />}
    </Box>

    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      gap="6px"
      mt="5px"
    >
      {status && <StatusTag colored outlined status={status} />}
      <Box display="flex" justifyContent="center" alignItems="center">
        <Text fontWeight={600} fontSize="12px" color={trioUITheme.colors['neutral-90']}>
          {formatDistanceToNowStrict(new Date(inStatusSince))}
        </Text>
      </Box>
    </Box>

    <Box display="flex" justifyContent="center" alignItems="center" mt="5px">
      <Text fontWeight={400} fontSize="12px" color={trioUITheme.colors['neutral-90']}>
        {owner}&apos;s
      </Text>
    </Box>

    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      gap="5px"
      mt="5px"
    >
      {stack &&
        stack.length > 0 &&
        stack.map((tech: string) => (
          <Box
            key={tech}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bg={trioUITheme.colors['neutral-30']}
            borderRadius="4px"
            px="6px"
            py="2px"
          >
            <Text fontWeight={400} fontSize="12px" color={trioUITheme.colors['neutral-90']}>
              {tech}
            </Text>
          </Box>
        ))}
    </Box>
  </Box>
);

export const CandidateBoardCardSkeleton = (): JSX.Element => (
  <Box
    display="flex"
    alignItems="flex-start"
    flexDirection="column"
    width="260px"
    bg={trioUITheme.colors['neutral-10']}
    boxShadow="0px 0px 6px rgba(0, 0, 0, 0.12)"
    border={`1px solid ${trioUITheme.colors['neutral-40']}`}
    borderRadius="8px"
    p="12px"
  >
    <Box display="flex" width="100%" justifyContent="flex-start" alignItems="flex-start">
      <SkeletonCircle w={6} h={6} className="tw-mr-3" />
      <Skeleton className="tw-h-4 tw-w-[100px] tw-my-1" />
    </Box>
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      gap="6px"
      mt="5px"
      height="26px"
    >
      <Skeleton className="tw-h-[18px] tw-w-[70px]" />
      <Skeleton className="tw-h-3 tw-w-[40px]" />
    </Box>
    <Box display="flex" justifyContent="center" alignItems="center" mt="5px">
      <Skeleton className="tw-h-2.5 tw-w-[50px] tw-my-1" />
    </Box>
    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      gap="5px"
      mt="5px"
    >
      <Skeleton className="tw-h-[22px] tw-w-[70px]" />
      <Skeleton className="tw-h-[22px] tw-w-[40px]" />
      <Skeleton className="tw-h-[22px] tw-w-[50px]" />
    </Box>
  </Box>
);

export default CandidateBoardCard;
